import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileUp,
  ArrowLeftRight,
  Building2,
  Sliders,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/ofx-imports', icon: FileUp, label: 'Importações OFX' },
  { to: '/bank-transactions', icon: ArrowLeftRight, label: 'Transações' },
  { to: '/bank-accounts', icon: Building2, label: 'Contas Bancárias' },
  { to: '/categorization-rules', icon: Sliders, label: 'Regras' },
  { to: '/settings', icon: Settings, label: 'Configurações' },
]

export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true' } catch { return false }
  })
  const location = useLocation()

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)) } catch { /* ignore */ }
  }, [collapsed])

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className={cn(
          'flex h-full shrink-0 flex-col bg-sidebar transition-[width] duration-300 ease-in-out',
          collapsed ? 'w-14' : 'w-60'
        )}
      >
        {/* Header: Logo + Toggle */}
        <div className={cn(
          'flex h-16 shrink-0 items-center border-b border-sidebar-border',
          collapsed ? 'flex-col justify-center gap-1 px-0 py-2' : 'justify-between px-3'
        )}>
          {/* Logo */}
          <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sidebar-primary">
              <span className="text-xs font-bold text-sidebar-primary-foreground">C</span>
            </div>
            {!collapsed && (
              <span className="overflow-hidden whitespace-nowrap text-sm font-semibold text-sidebar-foreground">
                Church Finance
              </span>
            )}
          </div>

          {/* Toggle */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCollapsed(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground transition-colors hover:bg-sidebar-hover"
                  aria-label="Expandir menu"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Expandir menu</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => setCollapsed(true)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground transition-colors hover:bg-sidebar-hover"
              aria-label="Recolher menu"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden p-2">
          {navItems.map(({ to, icon: Icon, label }) => {
            // Pre-compute active state as a plain string so TooltipTrigger asChild
            // doesn't serialize the className function (Radix slot merging bug).
            const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
            const collapsedClass = cn(
              'flex h-9 w-full items-center justify-center rounded-md transition-colors',
              isActive
                ? 'bg-sidebar-active text-sidebar-active-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-hover'
            )

            return collapsed ? (
              <Tooltip key={to}>
                <TooltipTrigger asChild>
                  <NavLink to={to} className={collapsedClass}>
                    <Icon className="h-4 w-4 shrink-0" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  {label}
                </TooltipContent>
              </Tooltip>
            ) : (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex h-9 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-active text-sidebar-active-foreground'
                      : 'text-sidebar-muted-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground'
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="overflow-hidden whitespace-nowrap">{label}</span>
              </NavLink>
            )
          })}
        </nav>
      </aside>
    </TooltipProvider>
  )
}
